import type { TopCategoryViewModel } from 'api/types/top-category';
import type { ProductTypeViewModel } from 'api/types/product-type';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ListingFilterState } from 'types/listingFilterState';
import Button from 'components/Button';
import { iconMap } from 'utils/constants/iconMap';

export interface SlidingCategoryNavigatorProps {
  topCategories: TopCategoryViewModel[];
  secondaryCategories: { id: number; name?: string | null }[];
  productTypes: ProductTypeViewModel[];
  draft: ListingFilterState;
  setDraftCategory: (level: 'top' | 'secondary' | 'productType', id: number | null) => void;
}

type PanelDepth = 0 | 1 | 2;

/**
 * Multi-level category picker with horizontal slide transitions (CSS transform).
 * Auto-skip for single-child levels is handled by setDraftCategory in useListingSearch; depth syncs to draft.
 */
export default function SlidingCategoryNavigator({
  topCategories,
  secondaryCategories,
  productTypes,
  draft,
  setDraftCategory,
}: SlidingCategoryNavigatorProps) {
  const [depth, setDepth] = useState<PanelDepth>(0);
  const [skippedSecondary, setSkippedSecondary] = useState(false);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(undefined);
  const panel0Ref = useRef<HTMLDivElement>(null);
  const panel1Ref = useRef<HTMLDivElement>(null);
  const panel2Ref = useRef<HTMLDivElement>(null);

  const syncDepthFromDraft = useCallback(() => {
    if (draft.topCategoryId == null) {
      setDepth(0);
      setSkippedSecondary(false);
      return;
    }
    const top = topCategories.find((t) => t.id === draft.topCategoryId);
    const secs = top?.secondaryCategories ?? [];
    if (secs.length <= 1) {
      setSkippedSecondary(secs.length === 1);
      setDepth(2);
      return;
    }
    setSkippedSecondary(false);
    if (draft.secondaryCategoryId == null) setDepth(1);
    else setDepth(2);
  }, [draft.topCategoryId, draft.secondaryCategoryId, topCategories]);

  useEffect(() => {
    syncDepthFromDraft();
  }, [syncDepthFromDraft]);

  const activePanelIndex: 0 | 1 | 2 = depth === 0 ? 0 : depth === 1 ? 1 : 2;

  const measureActivePanel = useCallback(() => {
    const el =
      activePanelIndex === 0 ? panel0Ref.current : activePanelIndex === 1 ? panel1Ref.current : panel2Ref.current;
    if (!el) return;
    setViewportHeight(el.getBoundingClientRect().height);
  }, [activePanelIndex]);

  useLayoutEffect(() => {
    measureActivePanel();
  }, [
    measureActivePanel,
    depth,
    topCategories,
    secondaryCategories,
    productTypes,
    draft.topCategoryId,
    draft.secondaryCategoryId,
    draft.productTypeId,
  ]);

  useEffect(() => {
    const el =
      activePanelIndex === 0 ? panel0Ref.current : activePanelIndex === 1 ? panel1Ref.current : panel2Ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measureActivePanel());
    ro.observe(el);
    return () => ro.disconnect();
  }, [activePanelIndex, measureActivePanel, topCategories, secondaryCategories, productTypes]);

  const handleBack = () => {
    if (depth === 0) return;
    if (depth === 2 && skippedSecondary) {
      setDraftCategory('top', null);
      return;
    }
    if (depth === 2 && !skippedSecondary) {
      setDraftCategory('productType', null);
      setDepth(1);
      return;
    }
    if (depth === 1) {
      setDraftCategory('top', null);
    }
  };

  const handleSelectTop = (id: number | null) => {
    setDraftCategory('top', id);
  };

  const handleSelectSecondary = (id: number | null) => {
    setDraftCategory('secondary', id);
    setDepth(2);
  };

  const handleSelectProductType = (id: number | null) => {
    setDraftCategory('productType', id);
  };

  const translatePct = depth === 0 ? 0 : depth === 1 ? -33.333 : -66.666;

  return (
    <div className="sliding-category-nav mb-2">
      {depth > 0 && (
        <Button
          type="button"
          className="btn btn-link text-decoration-none p-0 mb-2 d-flex align-items-center"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left me-2" />
          Geri
        </Button>
      )}
      <div
        className="sliding-category-viewport overflow-hidden rounded border"
        style={viewportHeight != null ? { height: viewportHeight, transition: 'height 0.2s ease-out', backgroundColor: 'var(--sb-bg)' } : { backgroundColor: 'var(--sb-bg)' }}
      >
        <div
          className="sliding-category-track d-flex align-items-start"
          style={{
            width: '300%',
            transform: `translateX(${translatePct}%)`,
            transition: 'transform 0.28s ease-out',
          }}
        >
          <div
            ref={panel0Ref}
            className="sliding-category-panel p-2"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="list-group list-group-flush">
              <Button
                type="button"
                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${draft.topCategoryId === null ? 'active' : ''}`}
                onClick={() => handleSelectTop(null)}
              >
                Tüm Kategoriler
                <i className="fas fa-chevron-right text-muted" />
              </Button>
              {topCategories.map((top) => (
                <Button
                  key={top.id}
                  type="button"
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${draft.topCategoryId === top.id ? 'active' : ''}`}
                  onClick={() => handleSelectTop(top.id)}
                >
                  {top.name ?? `Kategori ${top.id}`}
                  <i className="fas fa-chevron-right text-muted" />
                </Button>
              ))}
            </div>
          </div>
          <div
            ref={panel1Ref}
            className="sliding-category-panel p-2"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="list-group list-group-flush">
              {secondaryCategories.map((sec) => (
                <Button
                  key={sec.id}
                  type="button"
                  className={`list-group-item list-group-item-action ${draft.secondaryCategoryId === sec.id ? 'active fw-medium' : ''}`}
                  onClick={() => handleSelectSecondary(sec.id)}
                >
                  <i className="fas fa-angle-right me-2" />
                  {sec.name ?? `Alt ${sec.id}`}
                </Button>
              ))}
              {secondaryCategories.length === 0 && draft.topCategoryId != null && (
                <div className="text-muted small p-2">Bu kategoride alt seçenek yok.</div>
              )}
            </div>
          </div>
          <div
            ref={panel2Ref}
            className="sliding-category-panel p-2"
            style={{ width: '33.333%', flexShrink: 0 }}
          >
            <div className="list-group list-group-flush">
              {productTypes.map((pt) => (
                <Button
                  key={pt.id}
                  type="button"
                  className={`list-group-item list-group-item-action small ${draft.productTypeId === pt.id ? 'active fw-medium' : ''}`}
                  onClick={() => handleSelectProductType(pt.id)}
                >
                  <i className={`${iconMap[pt.id] ?? 'fas fa-microchip'} me-2`} />
                  {pt.name ?? `Ürün ${pt.id}`}
                </Button>
              ))}
              {productTypes.length === 0 && draft.secondaryCategoryId != null && (
                <div className="text-muted small p-2">Ürün tipi bulunamadı.</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {draft.productTypeId != null && (
        <div className="small text-success mt-2">
          <i className="fas fa-check-circle me-1" />
          Seçilen ürün tipi:{' '}
          <strong>
            {productTypes.find((p) => p.id === draft.productTypeId)?.name ?? `#${draft.productTypeId}`}
          </strong>
        </div>
      )}
    </div>
  );
}
